import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { mockData } from '../data/mock';
import axios from 'axios';
import emailService from '../services/emailService';
import { Autocomplete } from '@react-google-maps/api';
import MapRoute from './MapRoute';
import { PRICE_PER_KM } from '../data/mock';
import { v4 as uuidv4 } from 'uuid';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

const BookingForm = ({ isOpen, onClose }) => {
  const { t } = useThemeLanguage();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    service: '',
    pickupDate: '',
    pickupTime: '',
    pickupAddress: '',
    destination: '',
    passengers: '1',
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [distanceKm, setDistanceKm] = useState(0);
  const [estimatedPrice, setEstimatedPrice] = useState(0);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Refs pour Autocomplete
  const pickupAutocompleteRef = useRef(null);
  const destinationAutocompleteRef = useRef(null);

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const saveReservationToBackend = async (formData) => {
    try {
      const reservationData = {
        ...formData,
        passengers: parseInt(formData.passengers),
        reservation_id: formData.reservationId
      };
      const response = await axios.post(`${API}/reservations`, reservationData);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      throw error;
    }
  };

  const serviceTypes = [
    { value: 'transfer', label: t('booking.serviceTypes.transfer') },
    { value: 'airport', label: t('booking.serviceTypes.airportTrain') },
    { value: 'disposal', label: t('booking.serviceTypes.disposal') },
    { value: 'event', label: t('booking.serviceTypes.event') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.service || !formData.pickupDate || !formData.pickupTime || !formData.name || !formData.phone) {
      toast({
        title: t('booking.error'),
        description: t('booking.fillRequired'),
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }

    const reservationId = uuidv4();
    const estimatedPriceCalc = (distanceKm * PRICE_PER_KM).toFixed(2);
    const enrichedFormData = {
      ...formData,
      reservationId,
      distanceKm,
      estimatedPrice: estimatedPriceCalc
    };

    try {
      // WhatsApp
      const phoneNumber = process.env.REACT_APP_WHATSAPP_BUSINESS_NUMBER;
      const message = encodeURIComponent(
        "\u{1F697} *Nouvelle demande de réservation*\n\n" +
        "\u{1F194} *ID Réservation:* " + reservationId + "\n\n" +
        "\u{1F464} *Nom:* " + formData.name + "\n" +
        "\u{1F4F1} *Téléphone:* " + formData.phone + "\n" +
        "\u{2709} *Email:* " + formData.email + "\n\n" +
        "\u{1F699} *Service:* " + formData.service + "\n" +
        "\u{1F4C5} *Date:* " + formData.pickupDate + "\n" +
        "\u{1F550} *Heure:* " + formData.pickupTime + "\n" +
        "\u{1F4CD} *Départ:* " + formData.pickupAddress + "\n" +
        "\u{1F3AF} *Destination:* " + formData.destination + "\n" +
        "\u{1F465} *Passagers:* " + formData.passengers + "\n" +
        (distanceKm > 0 ? "\u{1F699} *Distance estimée:* " + distanceKm.toFixed(1) + " km\n" : "") +
        (distanceKm > 0 ? "\u{1F4B0} *Prix estimé:* €" + estimatedPriceCalc + "\n" : "") +
        (formData.notes ? "\u{1F4AC} *Notes:* " + formData.notes + "\n" : "")
      );
      window.open(`https://api.whatsapp.com/send/?phone=${phoneNumber}&text=${message}`, "_blank");

      // Emails
      await emailService.sendReservationEmail(enrichedFormData);
      await emailService.sendCustomerConfirmationEmail(enrichedFormData);

      toast({
        title: t('booking.reservationSent'),
        description: `${t('booking.requestSent')} #${reservationId}.`
      });
    } catch (emailError) {
      console.error('Erreur lors de l\'envoi des e-mails :', emailError);
      toast({
        title: t('booking.emailError'),
        description: t('booking.emailErrorDesc'),
        variant: "destructive"
      });
    }

    try {
      await saveReservationToBackend(enrichedFormData);
    } catch (dbError) {
      console.error('Erreur lors de la sauvegarde en base de données :', dbError);
      toast({
        title: t('booking.reservationSentSimple'),
        variant: "warning"
      });
    }

    setFormData({
      service: '',
      pickupDate: '',
      pickupTime: '',
      pickupAddress: '',
      destination: '',
      passengers: '1',
      name: '',
      phone: '',
      email: '',
      notes: ''
    });

    setTimeout(onClose, 2000);
    setIsSubmitting(false);
  };

  if (!isOpen) return null;

  const autocompleteOptions = {
    fields: ["formatted_address", "geometry", "name"],
    types: ["geocode", "establishment"],
    componentRestrictions: { country: ["fr","it"] }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800">
        <CardHeader className="border-b border-gray-100 dark:border-gray-700 relative">
          <CardTitle className="text-2xl font-light text-gray-900 dark:text-white text-center pr-8">{t('booking.title')}</CardTitle>
          <button 
            onClick={onClose} 
            type="button"
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <svg className="w-4 h-4 text-gray-600 dark:text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </CardHeader>

        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">

            {/* Service */}
            <div className="space-y-2">
              <Label htmlFor="service" className="dark:text-white">{t('booking.serviceType')}</Label>
              <Select onValueChange={(value) => handleInputChange('service', value)}>
                <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                  <SelectValue placeholder={t('booking.selectService')} />
                </SelectTrigger>
                <SelectContent>
                  {serviceTypes.map((service, idx) => (
                    <SelectItem key={idx} value={service.value}>{service.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date et Heure */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pickupDate" className="dark:text-white">{t('booking.pickupDate')}</Label>
                <Input
                  id="pickupDate"
                  type="date"
                  value={formData.pickupDate}
                  onChange={(e) => handleInputChange('pickupDate', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pickupTime" className="dark:text-white">{t('booking.time')}</Label>
                <Select onValueChange={(value) => handleInputChange('pickupTime', value)}>
                  <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                    <SelectValue placeholder={t('booking.time')} />
                  </SelectTrigger>
                  <SelectContent>
                    {mockData.timeSlots.map((time, idx) => (
                      <SelectItem key={idx} value={time}>{time}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Adresses */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pickupAddress" className="dark:text-white">{t('booking.pickupAddress')}</Label>
                <Autocomplete
                  onLoad={(autocomplete) => (pickupAutocompleteRef.current = autocomplete)}
                  onPlaceChanged={() => {
                    const place = pickupAutocompleteRef.current?.getPlace();
                    handleInputChange('pickupAddress', place?.formatted_address || '');
                  }}
                  options={autocompleteOptions}
                >
                  <Input
                    id="pickupAddress"
                    placeholder={t('booking.pickupPlaceholder')}
                    value={formData.pickupAddress}
                    onChange={(e) => handleInputChange('pickupAddress', e.target.value)}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </Autocomplete>
              </div>

              <div className="space-y-2">
                <Label htmlFor="destination" className="dark:text-white">{t('booking.destination')}</Label>
                <Autocomplete
                  onLoad={(autocomplete) => (destinationAutocompleteRef.current = autocomplete)}
                  onPlaceChanged={() => {
                    const place = destinationAutocompleteRef.current?.getPlace();
                    handleInputChange('destination', place?.formatted_address || '');
                  }}
                  options={autocompleteOptions}
                >
                  <Input
                    id="destination"
                    placeholder={t('booking.destinationPlaceholder')}
                    value={formData.destination}
                    onChange={(e) => handleInputChange('destination', e.target.value)}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </Autocomplete>
              </div>

              {/* Carte */}
              {formData.pickupAddress && formData.destination && (
                <>
                  <MapRoute
                    origin={formData.pickupAddress}
                    destination={formData.destination}
                    onDistanceChange={setDistanceKm}
                    onPriceChange={setEstimatedPrice}
                  />
                  {distanceKm > 0 && (
                    <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded flex justify-between text-sm font-medium dark:text-white">
                      <span>{t('booking.estimatedDistance')} : {distanceKm.toFixed(1)} km</span>
                      <span>{t('booking.estimatedPrice')} : {(distanceKm * PRICE_PER_KM).toFixed(2)}€</span>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Passagers */}
            <div className="space-y-2">
              <Label htmlFor="passengers" className="dark:text-white">{t('booking.passengers')}</Label>
              <Select onValueChange={(value) => handleInputChange('passengers', value)} defaultValue="1">
                <SelectTrigger className="dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4].map((num) => (
                    <SelectItem key={num} value={num.toString()}>
                      {num} {num > 1 ? t('booking.passengersPlural') : t('booking.passenger')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Infos perso */}
            <div className="border-t border-gray-100 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">{t('booking.yourInfo')}</h3>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="dark:text-white">{t('booking.fullName')}</Label>
                  <Input
                    id="name"
                    placeholder={t('booking.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[0-9]/g, '');
                      handleInputChange('name', value);
                    }}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="dark:text-white">{t('booking.phone')}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+33 6 12 34 56 78"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9+.,\s]/g, '');
                        handleInputChange('phone', value);
                      }}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="dark:text-white">{t('booking.email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes" className="dark:text-white">{t('booking.notes')}</Label>
                  <Textarea
                    id="notes"
                    placeholder={t('booking.notesPlaceholder')}
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-100 dark:border-gray-700">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1 dark:border-gray-600 dark:text-white" disabled={isSubmitting}>
                {t('booking.cancel')}
              </Button>
              <Button type="submit" className="flex-1 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100" disabled={isSubmitting}>
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white dark:border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                    {t('booking.sending')}
                  </div>
                ) : t('booking.sendRequest')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingForm;
